import React, { useState, useEffect } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToolbar,
  IonAvatar,
  IonSpinner
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { arrowBackOutline, searchOutline, personAddOutline } from 'ionicons/icons';
import { GamePlayer } from './types';
import { GolfAvatar } from '../ui/GolfAvatar';
import { api } from '@/utils/api';

interface PlayerEditorProps {
  nav: HTMLIonNavElement;
  existingPlayerIds: string[];
  onAddPlayer: (player: GamePlayer) => void;
  courseData?: {
    tees?: Array<{
      teeId: string;
      teeName: string;
      teeColor: string;
    }>
  };
}

const PlayerEditor: React.FC<PlayerEditorProps> = ({ nav, existingPlayerIds, onAddPlayer, courseData }) => {
  const { t } = useTranslation();

  const [segment, setSegment] = useState<'search' | 'guest'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [friends, setFriends] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Guest player state
  const [guestName, setGuestName] = useState('');
  const [guestHandicap, setGuestHandicap] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPlayers, setGuestPlayers] = useState<any[]>([]);
  const [isLoadingGuests, setIsLoadingGuests] = useState(false);
  const [isCreatingGuest, setIsCreatingGuest] = useState(false);

  // Load friends and guests when component mounts
  useEffect(() => {
    loadFriends();
    loadGuestPlayers();
  }, []);

  // Load guest players from API
  const loadGuestPlayers = async () => {
    setIsLoadingGuests(true);
    try {
      const response = await api.get('/guests');
      setGuestPlayers(response.data || []);
    } catch (error) {
      console.error('Error loading guest players:', error);
    } finally {
      setIsLoadingGuests(false);
    }
  };

  // Create a new guest player
  const createGuestPlayer = async () => {
    if (!guestName.trim()) return;

    setIsCreatingGuest(true);
    try {
      const newGuest = {
        name: guestName.trim(),
        handicap: guestHandicap ? parseFloat(guestHandicap) : 0,
        email: guestEmail.trim() || undefined
      };

      const response = await api.post('/guests', newGuest);

      // Add the new guest to the current game
      const { teeId, teeBox } = findBestTeeId();
      const guestPlayer: GamePlayer = {
        userId: `guest-${response.data.id}`,
        name: response.data.name,
        handicap: response.data.handicap || 0,
        teeBox: (teeBox === 'white' || teeBox === 'blue' || teeBox === 'yellow' || teeBox === 'red') ? teeBox : 'white',
        teeBoxId: teeId,
        teeName: courseData?.tees?.find((t: any) => t.teeId === teeId)?.teeName,
        startingHole: 1,
        isTemporary: false,
        email: response.data.email,
        guestId: response.data.id // Store the guest ID for future reference
      };

      onAddPlayer(guestPlayer);

      // Reset form
      setGuestName('');
      setGuestHandicap('');
      setGuestEmail('');

      // Refresh guest list
      await loadGuestPlayers();

      // Return to the previous screen
      nav.pop();
    } catch (error) {
      console.error('Error creating guest player:', error);
    } finally {
      setIsCreatingGuest(false);
    }
  };

  // Add an existing guest to the game
  const addExistingGuest = (guest: any) => {
    const { teeId, teeBox } = findBestTeeId();
    const guestPlayer: GamePlayer = {
      userId: `guest-${guest.id}`,
      name: guest.name,
      handicap: guest.handicap || 0,
      teeBox: (teeBox === 'white' || teeBox === 'blue' || teeBox === 'yellow' || teeBox === 'red') ? teeBox : 'white',
      teeBoxId: teeId,
      teeName: courseData?.tees?.find((t: any) => t.teeId === teeId)?.teeName,
      startingHole: 1,
      isTemporary: false,
      email: guest.email,
      guestId: guest.id
    };

    onAddPlayer(guestPlayer);
    nav.pop();
  };

  // Load friends from API
  const loadFriends = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/friendships/list');
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search query changes - used in the search functionality
  // Keeping this function as it might be used in the future
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (query.trim().length >= 2) {
      setIsLoading(true);
      setSearchPerformed(true);
      try {
        const response = await api.get(`/users/search?query=${encodeURIComponent(query)}`);
        setSearchResults(response.data || []);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsLoading(false);
      }
    } else if (query.trim().length === 0) {
      setSearchResults([]);
      setSearchPerformed(false);
    }
  };

  // Helper function to find the best tee ID from available tees
  const findBestTeeId = (): { teeId: string | undefined; teeBox: string } => {
    // No course data or tees available
    if (!courseData?.tees || courseData.tees.length === 0) {
      return { teeId: undefined, teeBox: 'white' };
    }

    // Try to find black tee first (typically longest)
    const blackTee = courseData.tees.find(t => t.teeColor.toLowerCase() === 'black');
    if (blackTee) {
      return { teeId: blackTee.teeId, teeBox: 'black' as const };
    }

    // If no black tee, try blue tee (often second longest)
    const blueTee = courseData.tees.find(t => t.teeColor.toLowerCase() === 'blue');
    if (blueTee) {
      return { teeId: blueTee.teeId, teeBox: 'blue' as const };
    }

    // Otherwise, use white tee (common default)
    const whiteTee = courseData.tees.find(t => t.teeColor.toLowerCase() === 'white');
    if (whiteTee) {
      return { teeId: whiteTee.teeId, teeBox: 'white' as const };
    }

    // Last resort: use the first available tee
    return {
      teeId: courseData.tees[0].teeId,
      teeBox: courseData.tees[0].teeColor.toLowerCase() as any
    };
  };

  // Convert a user profile to GamePlayer format
  const convertToGamePlayer = (user: any): GamePlayer => {
    // Find the best tee for this player
    const { teeId, teeBox } = findBestTeeId();

    return {
      userId: user.userId,
      name: user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.username || user.name,
      handicap: user.handicap || 0,
      teeBox: (teeBox === 'white' || teeBox === 'blue' || teeBox === 'yellow' || teeBox === 'red') ? teeBox : 'white', // Ensure valid tee color
      teeBoxId: teeId, // Store tee ID in the correct field
      teeName: courseData?.tees?.find((t: any) => t.teeId === teeId)?.teeName,
      startingHole: 1,
      imageUrl: user.imageUrl,
      isTemporary: false,
      email: user.email
    };
  };

  // Handle adding a player
  const handleAddPlayer = (user: any) => {
    const player = convertToGamePlayer(user);
    onAddPlayer(player);
    nav.pop();
  };

  // This function is kept for backward compatibility but is not currently used
  // as we now use the createGuestPlayer function instead
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddGuest = () => {
    if (guestName.trim()) {
      // Create a unique ID for the guest
      const newGuestId = `guest-${Date.now()}`;
      const { teeId, teeBox } = findBestTeeId();

      // Create the player object
      const newPlayer: GamePlayer = {
        userId: newGuestId,
        name: guestName.trim(),
        handicap: guestHandicap ? parseInt(guestHandicap) : 0,
        teeBox: (teeBox === 'white' || teeBox === 'blue' || teeBox === 'yellow' || teeBox === 'red') ? teeBox : 'white',
        teeBoxId: teeId,
        teeName: courseData?.tees?.find((t: any) => t.teeId === teeId)?.teeName,
        startingHole: 1,
        isTemporary: true,
        email: guestEmail || undefined
      };

      // Add the guest to the current game
      onAddPlayer(newPlayer);

      // Return to the previous screen
      nav.pop();
    }
  };

  // Filter friends based on search query and exclude already added players
  const filteredFriends = friends.filter(
    (friend) =>
    (searchQuery === '' ||
      (friend.username && friend.username.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (friend.firstName && friend.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (friend.lastName && friend.lastName.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className='bg-background'>
          <IonButtons slot="start">
            <IonBackButton defaultHref="#" text="" icon={arrowBackOutline} />
          </IonButtons>
          <IonTitle>{t('add_players')}</IonTitle>
        </IonToolbar>
        {/* <IonToolbar>
          <IonSearchbar
            value={searchQuery}
            onIonChange={(e) => handleSearch(e.detail.value || '')}
            placeholder={t('search_players')}
            className="p-0"
            debounce={300}
          />
        </IonToolbar> */}
        <IonToolbar className='bg-background'>
          <IonSegment className='w-full' value={segment} onIonChange={(e) => setSegment(e.detail.value as 'search' | 'guest')}>
            <IonSegmentButton value="search">
              <IonLabel>{t('friends')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="guest">
              <IonLabel>{t('guests')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {segment === 'search' ? (
          <>
            {isLoading ? (
              <div className="flex justify-center p-6">
                <IonSpinner name="crescent" />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Show search results if search is performed */}
                {searchPerformed && searchResults.length > 0 && (
                  <>
                    <div className="px-4 py-2">
                      <h3 className="text-sm font-medium text-muted-foreground">{t('search_results')}</h3>
                    </div>

                    {searchResults.map((user) => {
                      const isAdded = existingPlayerIds.includes(user.id || user.userId);

                      return (
                        <IonItem key={user.id || user.userId} className="rounded-lg">
                          <IonAvatar slot="start">
                            <GolfAvatar
                              size={40}
                              imageUrl={user.imageUrl}
                            />
                          </IonAvatar>
                          <IonLabel>
                            <h2>{user.firstName && user.lastName ?
                              `${user.firstName} ${user.lastName}` :
                              user.username}</h2>
                            <p>
                              {user.handicap !== undefined ?
                                `${t('handicap')}: ${user.handicap}` :
                                '@' + user.username}
                            </p>
                          </IonLabel>
                          {isAdded ? (
                            <IonButton slot="end" fill="outline" disabled>
                              {t('added')}
                            </IonButton>
                          ) : (
                            <IonButton
                              slot="end"
                              fill="solid"
                              className="font-bold"
                              onClick={() => handleAddPlayer(user)}
                            >
                            </IonButton>
                          )}
                        </IonItem>
                      );
                    })}
                  </>
                )}

                {/* Show no results message */}
                {searchPerformed && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                  <div className="text-center p-4">
                    <IonIcon icon={searchOutline} className="text-4xl text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">
                      {t('no_players_found')}
                    </p>
                    <IonText className="text-xs mt-1">
                      {t('try_different_search')}
                    </IonText>
                  </div>
                )}

                {/* Show friends list if not searching or search is empty */}
                {(!searchPerformed || searchQuery.trim().length < 2) && (
                  <>
                    {filteredFriends.length > 0 ? (
                      filteredFriends.map((friend) => {
                        const isAdded = existingPlayerIds.includes(friend.userId);
                        return (
                          <IonItem key={friend.userId} className="rounded-lg space-x-2">
                            <GolfAvatar
                              size={40}
                              imageUrl={friend.imageUrl}
                            />
                            <IonLabel>
                              <h2>{friend.firstName && friend.lastName ?
                                `${friend.firstName} ${friend.lastName}` :
                                friend.username}</h2>
                              <span className="text-muted-foreground text-sm">
                                {friend.handicap !== undefined ?
                                  `HCP: ${friend.handicap}` :
                                  '@' + friend.username}
                              </span>
                            </IonLabel>
                            {isAdded ? (
                              <IonButton slot="end" fill="outline" disabled>
                                {t('added')}
                              </IonButton>
                            ) : (
                              <IonButton
                                slot="end"
                                fill="solid"
                                shape='round'
                                className="font-bold"
                                onClick={() => handleAddPlayer(friend)}
                              >
                                {t('add')}
                              </IonButton>
                            )}
                          </IonItem>
                        );
                      })
                    ) : (
                      <div className="text-center p-4">
                        <p className="text-muted-foreground">
                          {t('no_friends_yet')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="p-4 space-y-6">
            <p className="text-muted-foreground text-sm text-center">
              {t('add_guest_player_description') || 'Add an unregistered player to your game. You can provide their email so they can view their stats after signing up.'}
            </p>

            {/* Add Photo Button - Optional for future use */}
            {/* <div className="flex justify-center mb-4">
              <div className="size-24 bg-gray-100 rounded-md flex items-center justify-center border-2 border-dashed border-gray-300">
                <IonIcon icon={addOutline} className="text-gray-400 text-3xl" />
              </div>
            </div>
            <div className="text-center mb-4">
              <p className="text-gray-500 text-sm">{t('add_photo')}</p>
            </div> */}

            {/* Guest Creation Form */}
            <div className="space-y-4">
              <div>
                <div className="mb-1 uppercase text-xs font-medium text-gray-500">
                  {t('player_name')} *
                </div>
                <IonInput
                  value={guestName}
                  onIonChange={(e) => setGuestName(e.detail.value || '')}
                  placeholder={t('first_name') + ' ' + t('last_name')}
                  className="bg-gray-100 rounded-lg p-3"
                />
              </div>

              <div>
                <div className="mb-1 uppercase text-xs font-medium text-gray-500">
                  {t('handicap')}
                </div>
                <IonInput
                  type="number"
                  value={guestHandicap}
                  onIonChange={(e) => setGuestHandicap(e.detail.value || '')}
                  placeholder="0"
                  className="bg-gray-100 rounded-lg p-3"
                />
              </div>

              <div>
                <div className="mb-1 uppercase text-xs font-medium text-gray-500">
                  {t('email')} ({t('optional')})
                </div>
                <IonInput
                  type="email"
                  value={guestEmail}
                  onIonChange={(e) => setGuestEmail(e.detail.value || '')}
                  placeholder="first.last@mail.com"
                  className="bg-gray-100 rounded-lg p-3"
                />
              </div>

              <IonButton
                expand="block"
                onClick={createGuestPlayer}
                disabled={!guestName.trim() || isCreatingGuest}
                className="mt-2"
              >
                {isCreatingGuest ? (
                  <IonSpinner name="crescent" className="mr-2" />
                ) : (
                  <IonIcon icon={personAddOutline} className="mr-2" />
                )}
                {t('GameSetup.create_guest')}
              </IonButton>
            </div>

            {/* List of existing guests */}
            {isLoadingGuests ? (
              <div className="flex justify-center p-4">
                <IonSpinner name="crescent" />
              </div>
            ) : guestPlayers.length > 0 ? (
              <div className="mt-8">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  {t('GameSetup.your_guests')}
                </h3>
                <div className="space-y-2">
                  {guestPlayers.map((guest) => (
                    <IonItem key={guest.id} className="rounded-lg">
                      <IonAvatar slot="start" className="bg-gray-100 flex items-center justify-center">
                        <IonIcon icon={personAddOutline} className="text-gray-400 text-xl" />
                      </IonAvatar>
                      <IonLabel>
                        <h2 className="font-medium">{guest.name}</h2>
                        <p className="text-sm text-muted-foreground">
                          {guest.handicap ? `${t('handicap')}: ${guest.handicap}` : t('GameSetup.no_handicap')}
                        </p>
                      </IonLabel>
                      <IonButton
                        slot="end"
                        fill="clear"
                        onClick={() => addExistingGuest(guest)}
                      >
                        {t('add')}
                      </IonButton>
                    </IonItem>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center p-4 text-muted-foreground text-sm">
                {t('GameSetup.no_guests_yet')}
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default PlayerEditor;
